import { API } from "./apiClient";
import { API_USER_EVENT } from "../../constants";

const eventAPI = API_USER_EVENT;
const eventsPath = "/events";

/**
 * Create a new event
 *
 * NOTE (security): identity (cognitoUsername / cognitoEmail) is intentionally
 * NOT set here anymore. Client-supplied identity is spoofable. The backend
 * Lambda must derive the verified identity from the authorizer claims
 * (event.requestContext.authorizer.claims, or .jwt.claims on HTTP API) and
 * ignore any identity fields in the body.
 *
 * @param {Object} eventData - Event data
 * @param {string} eventData.eventType - Type: 'assignment', 'note', 'match', 'group_match', 'detection'
 * @param {string} eventData.description - Description (required for assignments)
 * @param {string} eventData.status - Assignment status: 'open' or 'closed' (for assignment events)
 * @param {Object} eventData.location - Geographic location data
 * @returns {Promise<Object>} Created event
 */
export async function createEvent(eventData) {
  try {
    const response = await API.post(eventAPI, eventsPath, {
      body: eventData
    });
    return response;
  } catch (error) {
    console.error("Create event failed:", error);
    throw error;
  }
}

/**
 * Create an assignment event
 * @param {string} description - One line description
 * @param {Object} location - Geographic location (optional)
 * @param {string} status - Assignment status: 'open' or 'closed' (default: 'open')
 * @returns {Promise<Object>} Created event
 */
export async function createAssignment(description, location = null, status = 'open') {
  return createEvent({
    eventType: 'assignment',
    description,
    status,
    ...(location && { location })
  });
}

/**
 * Set an assignment's status.
 *
 * Renamed from toggleAssignmentStatus: this does not toggle, it sets the
 * status to whatever the caller passes. The caller is responsible for
 * computing the new value.
 *
 * @param {string} eventId - Assignment event ID
 * @param {string} newStatus - New status: 'open' or 'closed'
 * @returns {Promise<Object>} Updated event
 */
export async function setAssignmentStatus(eventId, newStatus) {
  return updateEvent(eventId, { status: newStatus });
}

/**
 * @deprecated Use setAssignmentStatus instead (more accurate name)
 * Toggle assignment status between 'open' and 'closed'
 * @param {string} eventId - Assignment event ID
 * @param {string} newStatus - New status: 'open' or 'closed'
 * @returns {Promise<Object>} Updated event
 */
export async function toggleAssignmentStatus(eventId, newStatus) {
  return setAssignmentStatus(eventId, newStatus);
}


/**
 * Create a face match event
 *
 * BREAKING CHANGE: now takes a single options object instead of 15 positional
 * args. Update callers from createMatchEvent(name, img, person, ...) to
 * createMatchEvent({ personName, personImage, person, ... }).
 *
 * @param {Object} opts
 * @param {string} opts.personName - Name of the matched person (required)
 * @param {string} opts.personImage - Image URL of the matched person
 * @param {Object} [opts.person] - Full person object with additional data
 * @param {Object} [opts.openAssignment] - Open assignment at time of match (backward compat)
 * @param {Object} [opts.location] - Geographic location
 * @param {string} [opts.matchSource='manual_filter'] - 'face_match' or 'manual_filter'
 * @param {number} [opts.matchAccuracy] - Match accuracy as 0-1 value (AI matches)
 * @param {string} [opts.note] - Additional note for the match
 * @param {string} [opts.capturedImageUrl] - URL of captured image used for AI match (face_match only)
 * @param {Object} [opts.boundingBox] - Face bounding box from Rekognition (multi-face)
 * @param {number} [opts.matchCount] - Number of faces matched in image (multi-face)
 * @param {number} [opts.detectedFaceCount] - Total faces detected in image (multi-face)
 * @param {string} [opts.groupEventId] - Parent group match event ID (multi-face)
 * @param {string} [opts.assignmentEventId] - Parent assignment event ID
 * @param {string} [opts.detectionEventId] - Parent detection event ID (links to complete face data)
 * @returns {Promise<Object>} Created event
 */
export async function createMatchEvent({
  personName,
  personImage,
  person = null,
  openAssignment = null,
  location = null,
  matchSource = 'manual_filter',
  matchAccuracy = null,
  note = null,
  capturedImageUrl = null,
  boundingBox = null,
  matchCount = null,
  detectedFaceCount = null,
  groupEventId = null,
  assignmentEventId = null,
  detectionEventId = null
} = {}) {
  try {
    const description = `Matched with ${personName}`;
    return await createEvent({
      eventType: 'match',
      description,
      metadata: {
        personName,
        personImage,
        matchSource,
        ...(matchAccuracy !== null && { matchAccuracy }),
        ...(person && { person }),
        ...(openAssignment && { openAssignment }),
        ...(assignmentEventId && { assignmentEventId }),
        ...(note && { note }),
        ...(capturedImageUrl && { capturedImageUrl }),
        ...(boundingBox && { boundingBox }),
        ...(matchCount !== null && { matchCount }),
        ...(detectedFaceCount !== null && { detectedFaceCount }),
        ...(groupEventId && { groupEventId }),
        ...(detectionEventId && { detectionEventId })
      },
      ...(location && { location })
    });
  } catch (error) {
    console.error("Create match event failed:", error);
    throw error;
  }
}

/**
 * Create a detection event (face detection results from captured image)
 *
 * This event represents the face detection step itself and stores ALL detected faces.
 * Created optimistically before matching. Not displayed in UI currently, but provides
 * complete face detection data for admin tools (Face Grouping Tester) and future features.
 *
 * @param {string} capturedImageUrl - URL of captured image
 * @param {Array} allFaceDetails - Array of all detected faces with bounding boxes
 * @param {Object} location - Geographic location (optional)
 * @param {Object} clusterMetadata - Clustering metadata from V3.2 algorithm (optional)
 * @returns {Promise<Object>} Created detection event
 */
export async function createDetectionEvent(capturedImageUrl, allFaceDetails = [], location = null, clusterMetadata = null) {
  const detectedFaceCount = allFaceDetails.length;
  const description = `Detected ${detectedFaceCount} face${detectedFaceCount !== 1 ? 's' : ''}`;

  return createEvent({
    eventType: 'detection',
    description,
    metadata: {
      capturedImageUrl,
      detectedFaceCount,
      allFaceDetails,
      ...(clusterMetadata && { clusterMetadata })
    },
    ...(location && { location })
  });
}

/**
 * Create a group match event (parent event for multi-face matches)
 *
 * BREAKING CHANGE: now takes a single options object instead of positional
 * args (which previously had a defaulted `location` ahead of required params,
 * making it impossible to omit). Update callers accordingly.
 *
 * @param {Object} opts
 * @param {number} opts.matchCount - Number of faces matched (required)
 * @param {number} opts.detectedFaceCount - Total faces detected (required)
 * @param {string} opts.capturedImageUrl - URL of captured image (required)
 * @param {Array<string>} [opts.personNames=[]] - Names of all matched people
 * @param {Object} [opts.location] - Geographic location
 * @param {string} [opts.assignmentEventId] - Parent assignment event ID
 * @param {string} [opts.detectionEventId] - Parent detection event ID
 * @returns {Promise<Object>} Created group match event
 */
export async function createGroupMatchEvent({
  matchCount,
  detectedFaceCount,
  capturedImageUrl,
  personNames = [],
  location = null,
  assignmentEventId = null,
  detectionEventId = null
} = {}) {
  try {
    const description = `Group photo: ${matchCount}/${detectedFaceCount} faces matched`;
    return await createEvent({
      eventType: 'group_match',
      description,
      metadata: {
        matchCount,
        detectedFaceCount,
        capturedImageUrl,
        personNames,
        matchSource: 'face_match',
        ...(assignmentEventId && { assignmentEventId }),
        ...(detectionEventId && { detectionEventId })
      },
      ...(location && { location })
    });
  } catch (error) {
    console.error("Create group match event failed:", error);
    throw error;
  }
}

/**
 * Get a specific event by ID
 * @param {string} eventId - Event ID
 * @returns {Promise<Object>} Event data
 */
export async function getEvent(eventId) {
  try {
    const response = await API.get(eventAPI, `/event/${eventId}`);
    return response;
  } catch (error) {
    console.error("Get event failed:", error);
    throw error;
  }
}

/**
 * Get cluster analysis for an event
 * Returns enriched cluster metadata with associated person matches
 *
 * @param {string} eventId - Event ID (detection or match event)
 * @returns {Promise<Object>} Cluster analysis data
 */
export async function getClusterAnalysis(eventId) {
  try {
    const response = await API.get(eventAPI, `/event/${eventId}/cluster-analysis`);
    console.log('[ClusterAnalysis] Response:', response);
    return response;
  } catch (error) {
    console.error("Get cluster analysis failed:", error);
    throw error;
  }
}

/**
 * List events for the current user
 * @param {Object} options - Query options
 * @param {string} options.eventType - Filter by event type
 * @param {number} options.limit - Max number of events to return (default: 20)
 * @param {string} options.nextToken - Token for pagination (from previous response)
 * @returns {Promise<Object>} Events list with { events: Array, nextToken: string|null }
 */
export async function listEvents(options = {}) {
  try {
    const queryParams = {};
    if (options.eventType) queryParams.eventType = options.eventType;
    if (options.status) queryParams.status = options.status;
    if (options.limit) queryParams.limit = options.limit;
    if (options.nextToken) queryParams.nextToken = options.nextToken;

    const response = await API.get(eventAPI, eventsPath, {
      queryStringParameters: queryParams
    });
    return response;
  } catch (error) {
    console.error("List events failed:", error);
    throw error;
  }
}

/**
 * Get all open assignments.
 *
 * Paginates through ALL pages because DynamoDB applies the status filter AFTER
 * the limit, so a single open assignment can sit past the first page and be
 * missed. Looping on nextToken makes this correct regardless of table size.
 *
 * Better long-term fix: back this with a GSI keyed on status so "open" is a
 * direct query instead of a scan-and-filter, and drop the loop.
 *
 * @returns {Promise<Array>} List of open assignments
 */
export async function getOpenAssignments() {
  try {
    const openAssignments = [];
    let nextToken = null;

    do {
      const response = await listEvents({
        eventType: 'assignment',
        status: 'open',
        limit: 100,
        ...(nextToken && { nextToken })
      });

      if (response.events?.length) {
        openAssignments.push(...response.events);
      }
      nextToken = response.nextToken || null;
    } while (nextToken);

    return openAssignments;
  } catch (error) {
    console.error("Get open assignments failed:", error);
    throw error;
  }
}

/**
 * Update an event
 * @param {string} eventId - Event ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated event
 */
export async function updateEvent(eventId, updates) {
  try {
    const response = await API.put(eventAPI, `/event/${eventId}`, {
      body: updates
    });
    return response;
  } catch (error) {
    console.error("Update event failed:", error);
    throw error;
  }
}

/**
 * Delete an event
 * @param {string} eventId - Event ID
 * @returns {Promise<void>}
 */
export async function deleteEvent(eventId) {
  try {
    await API.del(eventAPI, `/event/${eventId}`);
  } catch (error) {
    console.error("Delete event failed:", error);
    throw error;
  }
}

/**
 * Get current geographic location using user settings
 * @param {Object} settings - User settings from SettingsContext
 * @param {boolean} settings.locationHighAccuracy - Use GPS for better accuracy
 * @param {number} settings.locationAcquisitionTimeout - Timeout in milliseconds
 * @param {number} settings.locationCacheMaxAge - Max age of cached position in milliseconds
 * @returns {Promise<Object|null>} Location object with latitude, longitude, accuracy, timestamp or null if unavailable
 */
export async function getCurrentLocation(settings) {
  if (!settings) {
    console.warn('getCurrentLocation called without settings');
    return null;
  }

  const {
    locationHighAccuracy = true,
    locationAcquisitionTimeout = 10000,
    locationCacheMaxAge = 0
  } = settings;

  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      console.warn('Geolocation not available');
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp).toISOString()
        });
      },
      (error) => {
        console.warn('Geolocation error:', error.message);
        resolve(null);
      },
      {
        enableHighAccuracy: locationHighAccuracy,
        timeout: locationAcquisitionTimeout,
        maximumAge: locationCacheMaxAge
      }
    );
  });
}
